import React, { createContext, useContext, useState, useEffect } from "react";
import { clearCustomerCart, clearOldGenericCart } from "../utils/cartUtils";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    console.error("useAuth must be used within an AuthProvider");
    // Return a fallback context to prevent crashes
    return {
      user: null,
      token: null,
      loading: false,
      login: () =>
        Promise.resolve({
          success: false,
          error: "Auth context not available",
        }),
      loginCustomer: () => {},
      loginManager: () =>
        Promise.resolve({
          success: false,
          error: "Auth context not available",
        }),
      loginCustomerManager: () => {},
      register: () =>
        Promise.resolve({
          success: false,
          error: "Auth context not available",
        }),
      logout: () => {},
      createAdmin: () =>
        Promise.resolve({
          success: false,
          error: "Auth context not available",
        }),
      updateProfile: () =>
        Promise.resolve({
          success: false,
          error: "Auth context not available",
        }),
      updateUser: () => {},
      isAuthenticated: false,
      isAdmin: false,
      isCustomer: false,
      isManager: false,
      isCustomerManager: false,
      isSalesExecutive: false,
    };
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem("token"));

  // Define logout function first to avoid circular dependency
  const logout = () => {
    // Clear customer-specific cart if user is a customer
    if (user?.type === "customer" && user?.id) {
      clearCustomerCart(user.id);
    }
    setUser(null);
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    // Redirect to landing page
    window.location.href = "/";
  };

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem("token");
        const storedUser = localStorage.getItem("user");

        if (storedToken && storedUser) {
          try {
            // Parse stored user
            const parsedUser = JSON.parse(storedUser);

            // Set user from localStorage without API verification initially
            setUser(parsedUser);
            setToken(storedToken);

            // Optionally verify token in background (non-blocking)
            // Import apiService dynamically to avoid circular dependency
            const { default: apiService } = await import("../services/api");

            // Add a small delay to avoid immediate verification loop
            setTimeout(() => {
              apiService.getProfile().catch((error) => {
                console.error("Background token verification failed:", error);
                // Only clear if verification explicitly fails with 401/403
                if (
                  error.message.includes("401") ||
                  error.message.includes("403") ||
                  error.message.includes("Unauthorized")
                ) {
                  console.log("Token expired or invalid, logging out");
                  logout();
                }
              });
            }, 1000);
          } catch (error) {
            console.error("Error parsing stored user:", error);
            localStorage.removeItem("token");
            localStorage.removeItem("user");
          }
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []); // Remove logout dependency to avoid circular dependency

  const login = async (email, password) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.login(email, password);

      // For admin login, ensure the user object has the correct structure
      let userObj;
      if (data.user) {
        // Admin login - data.user contains the user info
        userObj = {
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          role: data.user.role || "admin",
        };
      } else if (data.customer) {
        // Customer login
        userObj = {
          id: data.customer.id,
          email: data.customer.email,
          name: data.customer.name,
          role: "customer",
        };
      } else {
        throw new Error("Invalid login response");
      }

      setUser(userObj);
      setToken(data.token);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(userObj));

      // Clear any old generic cart data when customer logs in
      if (userObj.role === "customer") {
        clearOldGenericCart();
      }

      return { success: true, role: userObj.role };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginCustomer = async (email, password) => {
    try {
      console.log("loginCustomer called with:", { email, password });
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.customerLogin(email, password);

      console.log("Customer login API response:", data);

      let userObj = data.customer || {};
      userObj.role = "customer";
      userObj.type = "customer";

      console.log("Normalized user object:", userObj);

      setUser(userObj);
      setToken(data.token);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(userObj));

      // Clear any old generic cart data when customer logs in
      clearOldGenericCart();

      console.log("Customer login successful, user set:", userObj);
      return { success: true, role: "customer" };
    } catch (error) {
      console.error("Customer login error:", error);
      return { success: false, error: error.message };
    }
  };

  const loginManager = async (email, password) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.managerLogin(email, password);
      let userObj = data.user || {};
      userObj.role = data.role;
      setUser(userObj);
      setToken(data.token);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(userObj));
      return { success: true, role: data.role };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const loginCustomerManager = async (token, customerManager) => {
    const normalizedManager = {
      ...customerManager,
      role: "customer_manager",
      type: "customer_manager",
    };

    setUser(normalizedManager);
    setToken(token);
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(normalizedManager));
  };

  const loginSalesExecutive = async (email, password) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.salesExecutiveLogin(email, password);

      // Ensure the user object has the correct structure for sales executive
      let userObj = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role || "sales_executive",
      };

      setUser(userObj);
      setToken(data.token);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(userObj));

      return { success: true, role: userObj.role };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.register(userData);
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const createAdmin = async (adminData) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.createAdmin(adminData);
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateProfile = async (profileData) => {
    try {
      // Import apiService dynamically to avoid circular dependency
      const { default: apiService } = await import("../services/api");
      const data = await apiService.updateProfile(profileData);
      setUser(data.user);
      localStorage.setItem("user", JSON.stringify(data.user));
      return { success: true, message: data.message };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const updateUser = (updatedUser, callback = null) => {
    console.log("Updating user state:", updatedUser);
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));

    // Execute callback after state update if provided
    if (callback) {
      setTimeout(callback, 0);
    }
  };

  // Fixed role checking logic
  const isAdmin = user?.role === "portal_admin" || user?.role === "admin";
  const isCustomer = user?.role === "customer";
  const isManager = user?.role === "manager";
  const isCustomerManager = user?.role === "customer_manager";
  const isSalesExecutive = user?.role === "sales_executive";
  const isEmployee = user?.role === "employee";

  // Debug logging for role checking (only log when user changes)
  useEffect(() => {
    if (user) {
      console.log("AuthContext - User logged in:", {
        userRole: user?.role,
        userType: typeof user?.role,
        userName: user?.name,
        userEmail: user?.email,
        isAdmin,
        isCustomer,
        isManager,
        isCustomerManager,
        isSalesExecutive,
        isEmployee,
        isAuthenticated: !!user,
      });
    } else {
      console.log("AuthContext - No user authenticated");
    }
  }, [
    user,
    isAdmin,
    isCustomer,
    isManager,
    isCustomerManager,
    isSalesExecutive,
    isEmployee,
  ]);

  const value = {
    user,
    token,
    loading,
    login,
    loginCustomer,
    loginManager,
    loginCustomerManager,
    loginSalesExecutive,
    register,
    logout,
    createAdmin,
    updateProfile,
    updateUser,
    isAuthenticated: !!user,
    isAdmin,
    isCustomer,
    isManager,
    isCustomerManager,
    isSalesExecutive,
    isEmployee,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};
